import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useAuth } from '../context/AuthContext'
import { Flame, User, Users } from 'lucide-react'

export default function Login() {
  const [userType, setUserType] = useState<'student' | 'parent'>('student')
  const [email, setEmail] = useState('')
  const [username, setUsername] = useState('')
  const [password, setPassword] = useState('')
  const [pin, setPin] = useState('')
  const [usePin, setUsePin] = useState(false)
  const [error, setError] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const { login } = useAuth()
  const navigate = useNavigate()

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError('')
    setIsLoading(true)

    try {
      await login({
        email: userType === 'parent' ? email : undefined,
        username: userType === 'student' ? username : undefined,
        password: usePin ? '' : password,
        pin: usePin ? pin : undefined,
        user_type: userType,
      })
      navigate(userType === 'parent' ? '/parent' : '/student')
    } catch (err: any) {
      setError(err.response?.data?.error || 'Login failed')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-base p-4">
      <div className="max-w-md w-full">
        {/* Logo */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 text-4xl font-bold text-quest-primary">
            <Flame className="w-10 h-10 text-orange-500 animate-fire" />
            QUEST
          </div>
          <p className="mt-2 text-gray-400">Level Up Your Learning</p>
        </div>

        {/* User type tabs */}
        <div className="flex mb-6 bg-surface-card rounded-lg p-1">
          <button
            onClick={() => setUserType('student')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg transition-colors ${
              userType === 'student'
                ? 'bg-quest-primary text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <User className="w-5 h-5" />
            Student
          </button>
          <button
            onClick={() => setUserType('parent')}
            className={`flex-1 flex items-center justify-center gap-2 py-3 rounded-lg transition-colors ${
              userType === 'parent'
                ? 'bg-quest-primary text-white'
                : 'text-gray-400 hover:text-white'
            }`}
          >
            <Users className="w-5 h-5" />
            Parent
          </button>
        </div>

        {/* Login form */}
        <form onSubmit={handleSubmit} className="card">
          {error && (
            <div className="mb-4 p-3 bg-red-900/50 border border-red-700 rounded-lg text-red-300 text-sm">
              {error}
            </div>
          )}

          {userType === 'parent' ? (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Email
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg
                           text-white placeholder-gray-400 focus:outline-none focus:border-quest-primary"
                placeholder="parent@example.com"
                required
              />
            </div>
          ) : (
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Username
              </label>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg
                           text-white placeholder-gray-400 focus:outline-none focus:border-quest-primary"
                placeholder="Your username"
                required
              />
            </div>
          )}

          {userType === 'student' && (
            <div className="mb-4">
              <label className="flex items-center gap-2 text-sm text-gray-400 cursor-pointer">
                <input
                  type="checkbox"
                  checked={usePin}
                  onChange={(e) => setUsePin(e.target.checked)}
                  className="rounded bg-gray-700 border-gray-600 text-quest-primary focus:ring-quest-primary"
                />
                Use PIN instead
              </label>
            </div>
          )}

          {usePin && userType === 'student' ? (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                PIN
              </label>
              <input
                type="password"
                value={pin}
                onChange={(e) => setPin(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg
                           text-white text-center text-2xl tracking-widest
                           focus:outline-none focus:border-quest-primary"
                placeholder="****"
                maxLength={6}
                required
              />
            </div>
          ) : (
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Password
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full px-4 py-3 bg-gray-700 border border-gray-600 rounded-lg
                           text-white placeholder-gray-400 focus:outline-none focus:border-quest-primary"
                placeholder="Your password"
                required
              />
            </div>
          )}

          <button
            type="submit"
            disabled={isLoading}
            className="w-full btn btn-primary py-3 text-lg disabled:opacity-50"
          >
            {isLoading ? 'Logging in...' : 'Enter Quest'}
          </button>

          {userType === 'parent' && (
            <p className="mt-4 text-center text-sm text-gray-400">
              Don't have an account?{' '}
              <a href="/register" className="text-quest-primary hover:underline">
                Create one
              </a>
            </p>
          )}
        </form>
      </div>
    </div>
  )
}
